import React, { useMemo } from 'react';
import {
  getLablesFromChartData,
  getTitrationChartValues,
  roundedFloat,
} from './utils/utils';
import {
  useBufferDataPointErrors,
  useIngredientBufferDataPoints,
  useIngredientInfo,
} from 'redux/hooks/IngredientDetail';
import { BufferCalculationType } from 'business/BufferCalculationType';
import { setIngredientInfoByKey } from 'redux/reducers/ingredientDetailReducer';
import { useRMDispatch } from 'redux/hooks';
import { BufferDataTable } from './BufferDataTable';
import { IngredientChart } from './IngredientChart';
import { FormInputRow } from 'components/input';
import { BUFFER_CALCULATION_TYPES } from './utils/Constants';

export const TitrationCurve = () => {
  const ingInfo = useIngredientInfo();
  const bufferDataPoints = useIngredientBufferDataPoints();
  const bufferDataErrors = useBufferDataPointErrors();
  const dispatch = useRMDispatch();

  const chartData = useMemo(() => {
    if (ingInfo.bufferCalculationTypeId === BufferCalculationType.pKa.ID) {
      return getTitrationChartValues(parseFloat(ingInfo.pKa + ''));
    } else {
      const sortedPoints = [...bufferDataPoints].sort((a, b) => a.ph - b.ph);
      return sortedPoints.map((p) => {
        return { x: roundedFloat(p.ph), y: roundedFloat(p.ratio) };
      });
    }
  }, [ingInfo.pKa, ingInfo.bufferCalculationTypeId, bufferDataPoints]);

  const axisLabels = useMemo(() => getLablesFromChartData(chartData), [chartData]);

  const handleCalculationTypeChange = (e) => {
    dispatch(
      setIngredientInfoByKey({
        key: 'bufferCalculationTypeId',
        value: parseInt(e.target.value),
      }),
    );
  };

  const handlePKAChange = (e) => {
    dispatch(setIngredientInfoByKey({ key: 'pKa', value: e.target.value }));
  };

  return (
    <div
      id="titrationcurve-main-container"
      className="dashed-border basis-full max-h-full flex flex-row"
    >
      <div id="titrationcurve-sub-container" className="flex flex-col basis-7/12">
        <div id="titrationcurve-title" className="basis-1/6 p-2 font-bold self-start">
          Titration Curve
        </div>
        <div id="titrationcurve-chart" className="basis-5/6 h-5/6">
          <IngredientChart
            xLabels={axisLabels.x}
            yLabels={axisLabels.y}
            chartData={chartData}
            xTitle="pH"
            yTitle="Acid/Base Ratio"
          />
        </div>
      </div>
      <div
        id="titrationcurve-forminput-buffer-wrapper"
        className="flex basis-5/12 p-2 flex-col gap-2"
      >
        <div
          id="titrationcurve-calculationtype"
          className="flex flex-row basis-1/12"
        >
          <FormInputRow
            id='titrationcurve-forminput-'
            labeltext="Calculation Type"
            value={ingInfo.bufferCalculationTypeId}
            onChange={handleCalculationTypeChange}
            containerStyle={{ width: '100%' }}
            labelStyle={{ flexBasis: '70%' }}
            inputType="select"
            options={BUFFER_CALCULATION_TYPES}
          />
        </div>
        <div id="titrationcurve-pka" className="flex basis-1/12 flex-row">
          <FormInputRow
            id="titrationcurve-forminput-pka"
            labeltext="pKa"
            value={ingInfo?.pKa}
            onChange={handlePKAChange}
            disabled={ingInfo.bufferCalculationTypeId !== BufferCalculationType.pKa.ID}
            containerStyle={{ width: '100%' }}
            labelStyle={{ flexBasis: '70%' }}
            type="number"
          />
        </div>
        {ingInfo.bufferCalculationTypeId === BufferCalculationType.Interpolate.ID && (
          <BufferDataTable
            ingId={ingInfo.id}
            bufferDataPoints={bufferDataPoints}
            errors={bufferDataErrors}
          />
        )}
      </div>
    </div>
  );
};
